/* eslint-disable no-param-reassign */
import { Services } from 'pmg-services';
import { Datetime } from 'vue-datetime';
import 'vue-datetime/dist/vue-datetime.css';

import Vue from 'vue';
import VueOffline from 'vue-offline';
import VueBbob from '@bbob/vue2';
import VueTour from 'vue-tour';

import * as Sentry from '@sentry/vue';
import { BrowserTracing } from '@sentry/tracing';

import { store } from '@/app/shared/stores/store';
import { Events } from '@/app/shared/events';
import i18n from '@/i18n';
import router from '@/app/app-routes';
import App from '@/app/app.vue';
import moment from 'moment';
import Vue2TouchEvents from 'vue2-touch-events';
import VueCompositionAPI from '@vue/composition-api';
import mixpanel from 'mixpanel-browser';

require('vue-tour/dist/vue-tour.css');

Vue.config.productionTip = false;
Vue.prototype.$services = new Services({
  apiUrl: `${process.env.VUE_APP_API_URL}/api/`,
  withCredentials: true,
  nodeEnv: process.env.VUE_APP_ENV,
  redirect401: process.env.VUE_APP_ENV_RESTRICTED !== 'true',
  unauthorizedRoute: `${process.env.VUE_APP_BASE_URL}Login`,
  stripePK: process.env.VUE_APP_STRIPE_API_KEY,
});

Vue.prototype.$moment = moment;
Vue.prototype.$event = new Events(Vue.prototype);

Vue.use(VueCompositionAPI);
Vue.use(Datetime);
Vue.use(Vue2TouchEvents);
Vue.use(VueTour);

// pretty sure where it was before it was being applied globally anyway
Vue.use(VueBbob);

// To-do: no need to inject into every component
// https://www.npmjs.com/package/vue-offline
Vue.use(VueOffline);

Vue.directive('click-outside', {
  bind(el, binding, vnode) {
    el.eventSetDrag = () => {
      el.setAttribute('data-dragging', 'yes');
    };
    el.eventClearDrag = () => {
      el.removeAttribute('data-dragging');
    };
    el.eventOnClick = (event) => {
      const dragging = el.getAttribute('data-dragging');
      // Check that the click was outside the el and its children, and wasn't a drag
      if (!(el === event.target || el.contains(event.target)) && !dragging) {
        // call method provided in attribute value
        vnode.context[binding.expression](event);
      }
    };
    document.addEventListener('touchstart', el.eventClearDrag);
    document.addEventListener('touchmove', el.eventSetDrag);
    document.addEventListener('click', el.eventOnClick);
    document.addEventListener('touchend', el.eventOnClick);
  },
  unbind(el) {
    document.removeEventListener('touchstart', el.eventClearDrag);
    document.removeEventListener('touchmove', el.eventSetDrag);
    document.removeEventListener('click', el.eventOnClick);
    document.removeEventListener('touchend', el.eventOnClick);
    el.removeAttribute('data-dragging');
  },
});

Vue.directive('local-img', {
  // When the bound element is inserted into the DOM...
  inserted: (el, binding) => {
    if (binding.value.includes('https://')) {
      el.src = binding;
    } else {
      el.src = `${process.env.VUE_APP_BASE_URL}img/${binding.value}`;
    }
  },
});

Vue.directive('local-img-bg', {
  // When the bound element is inserted into the DOM...
  inserted: (el, binding) => {
    const backgroundText = binding.value.match(/(?=url\().*?(?=\))/);
    backgroundText[0] = backgroundText[0].replace('"', '');
    backgroundText[0] = backgroundText[0].replace('url(', '');
    const backgroundTextToUse = binding.value.replace(
      backgroundText[0],
      `"${process.env.VUE_APP_BASE_URL}img/${backgroundText[0]}"`,
    );
    const cssToUse = `background-image: ${backgroundTextToUse}`;
    el.style.cssText += cssToUse;
  },
});
Vue.prototype.$logError = (err) => {
  console.log(err);
};

Vue.prototype.$isProduction = process.env.VUE_APP_ENV === 'production';

Vue.prototype.$isStandard = process.env.VUE_APP_PMG_ENV === 'Standard';
Vue.prototype.$isNine = process.env.VUE_APP_PMG_ENV === 'IX';

let envTier = 1;
if (process.env.VUE_APP_PMG_ENV === 'Standard') {
  envTier = 1;
} else if (process.env.VUE_APP_PMG_ENV === 'IX') {
  envTier = 3;
}
Vue.prototype.$envTier = envTier;

Vue.prototype.$isMobileApp = process.env.VUE_APP_ENV === 'cordova';
Vue.prototype.$isMobile = Vue.observable({
  value: process.env.VUE_APP_ENV === 'cordova' || window.innerWidth < 924,
});

Vue.prototype.$isIOS = process.env.VUE_APP_ENV === 'cordova' && process.env.VUE_APP_CORDOVA_PLATFORM === 'ios';
Vue.prototype.$isAndroid = process.env.VUE_APP_ENV === 'cordova' && process.env.VUE_APP_CORDOVA_PLATFORM === 'android';

// maybe wrong but easy and feels safe
store.$isNine = Vue.prototype.$isNine;
store.$isStandard = Vue.prototype.$isStandard;
store.$isIOS = Vue.prototype.$isIOS;
store.$isMobileApp = Vue.prototype.$isMobileApp;

const vue = new Vue({
  router,
  store,
  i18n,
  render: h => h(App),
});

vue.$mount('#app');

if (process.env.NODE_ENV === 'production' && process.env.VUE_APP_ENV !== 'cordova') {
  Sentry.init({
    Vue,
    dsn: 'https://1265d2005dda49dc90069f385d063cca@o1203955.ingest.sentry.io/6330579',
    integrations: [
      new BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
        tracingOrigins: ['playmore.golf', 'account.playmore.golf', /^\//],
      }),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
  });
}

Vue.prototype.$mixpanel = mixpanel;
Vue.prototype.$action = (event) => { console.log(`Event ${event}`); };

function initMixpanel() {
  Vue.prototype.$mixpanel.init('3662a5b3cb2c2b138704ddc6ceef75dd', {
    debug: true,
    track_pageview: true,
    persistence: 'localStorage',
    autocapture: true,
  });
  Vue.prototype.$mixpanel.track('login');
  Vue.prototype.$action = (event) => {
    Vue.prototype.$mixpanel.track(event);
  };
}

function getHubspotCookie() {
  let hutkId;
  const value = `; ${document.cookie}`;
  // eslint-disable-next-line no-useless-concat
  const parts = value.split('; ' + 'hubspotutk' + '=');
  if (parts.length === 2) {
    // eslint-disable-next-line no-unused-vars, no-return-assign
    return (hutkId = parts.pop().split(';').shift());
  }
  return null;
}

let checkIndex = 0;
function startHSCheck() {
  if (getHubspotCookie() !== null) {
    initMixpanel();
  } else {
    setTimeout(() => {
      if (getHubspotCookie() !== null) {
        initMixpanel();
      } else {
        try {
          // eslint-disable-next-line no-undef
          _hsp.push(['addPrivacyConsentListener', (consent) => {
            if (consent.allowed) {
              initMixpanel();
            }
          }]);
        } catch (err) {
          checkIndex += 1;
          if (checkIndex <= 10) {
            setTimeout(startHSCheck(), 1500);
          }
        }
      }
    }, 1500);
  }
}
if (process.env.VUE_APP_ENV !== 'cordova' && process.env.NODE_ENV === 'production') {
  startHSCheck();
}

// Vue.use(VueGtm, {
//   id: 'GTM-MNTLS9S', // Your GTM single container ID, array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy'] or array of objects [{id: 'GTM-xxxxxx', queryParams: { gtm_auth: 'abc123', gtm_preview: 'env-4', gtm_cookies_win: 'x'}}, {id: 'GTM-yyyyyy', queryParams: {gtm_auth: 'abc234', gtm_preview: 'env-5', gtm_cookies_win: 'x'}}], // Your GTM single container ID or array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy']
//   defer: false, // Script can be set to `defer` to speed up page load at the cost of less accurate results (in case visitor leaves before script is loaded, which is unlikely but possible). Defaults to false, so the script is loaded `async` by default
//   compatibility: false, // Will add `async` and `defer` to the script tag to not block requests for old browsers that do not support `async`
//   debug: process.env.NODE_ENV !== 'production', // Whether or not display console logs debugs (optional)
//   loadScript: true, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
//   vueRouter: router, // Pass the router instance to automatically sync with router (optional)
//   trackOnNextTick: false, // Whether or not call trackView in Vue.nextTick
// });
